import { getAllBillingPeriodsOptions, getAllVehiclesOptions, getRequirementSubintervalsOptions } from '@/api/main'
import { Box, Button, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { AutoScheduleTabList } from './AutoScheduleTabList'
import { CopyMode } from './CopyMode'
import { DistrictsMode } from './DistrictsMode'
import { IntervalPicker } from './IntervalPicker'
import { RequirementsMode } from './RequirementsMode'
import { isIntervalDirty, isModalDirty, shouldConfirmCancel } from '@/utils/autoScheduleDirty'
import { getTimezone } from '@/utils/datetime'
import { findBillingPeriodForInterval, getOverlappingBillingPeriods } from '@/utils/scheduling'
import {
  getAutoScheduleFlags,
  isAutoScheduleAvailable,
  visibleAutoScheduleTabs,
  type AutoScheduleTabId,
} from '@/utils/autoScheduleFlags'
import { buildIntervalPresets, defaultPopulateInterval, type TimeInterval } from '@/utils/autoScheduleIntervals'
import { autoScheduleConfirmZIndex, autoScheduleModalZIndex } from './autoScheduleLayers'
import {
  buildRequirementIntervalChips,
  uniqueDriveTypeCombinations,
  type DriveTypeCombination,
} from '@/utils/autoScheduleRequirements'

dayjs.extend(utc)
dayjs.extend(timezone)

export function AutoScheduleModal({
  vehicleId,
  viewStart,
  viewEnd,
  initialInterval,
  initialMode,
  onClose,
  onModeChange,
}: {
  vehicleId: string
  viewStart: string
  viewEnd: string
  initialInterval?: TimeInterval
  initialMode?: AutoScheduleTabId
  onClose: () => void
  onModeChange?: (mode: AutoScheduleTabId) => void
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const tz = getTimezone()
  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())
  const { data: billingPeriods } = useSuspenseQuery(getAllBillingPeriodsOptions())
  const overlapping = useMemo(
    () => getOverlappingBillingPeriods(viewStart, viewEnd, billingPeriods),
    [viewStart, viewEnd, billingPeriods]
  )
  const [interval, setInterval] = useState<TimeInterval>(
    () => initialInterval ?? defaultPopulateInterval(viewStart, viewEnd)
  )
  const openedInterval = useRef(interval).current
  const [dirtyByTab, setDirtyByTab] = useState<Partial<Record<AutoScheduleTabId, boolean>>>({})
  const dirty = isModalDirty(isIntervalDirty(interval, openedInterval), Object.values(dirtyByTab).some(Boolean))
  const billingPeriodId = findBillingPeriodForInterval(interval.start, interval.end, overlapping)
  const flags = getAutoScheduleFlags()
  const tabs = visibleAutoScheduleTabs(flags, billingPeriodId !== null)
  const [mode, setMode] = useState<AutoScheduleTabId>(() => {
    if (initialMode && tabs.includes(initialMode)) {
      return initialMode
    }
    return tabs[0] ?? 'districts'
  })

  const vehicle = vehicles.find(item => item.id === vehicleId)
  const headerDate = dayjs.utc(viewStart).tz(tz).format('D. M. YYYY')

  const changeMode = (next: AutoScheduleTabId) => {
    setMode(next)
    onModeChange?.(next)
  }

  useEffect(() => {
    if (billingPeriodId !== null) {
      return
    }
    setDirtyByTab(current => (current.requirements ? { ...current, requirements: false } : current))
  }, [billingPeriodId])

  const requestClose = () => {
    if (!shouldConfirmCancel(dirty)) {
      onClose()
      return
    }
    modals.openConfirmModal({
      title: t('autoSchedule.confirmCancelTitle'),
      centered: true,
      zIndex: autoScheduleConfirmZIndex,
      children: <Text size="sm">{t('autoSchedule.confirmCancelBody')}</Text>,
      labels: { confirm: t('autoSchedule.leave'), cancel: t('autoSchedule.stay') },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    })
  }

  const activeMode = tabs.includes(mode) ? mode : tabs[0]
  const presets = useMemo(() => buildIntervalPresets(viewStart, viewEnd), [viewStart, viewEnd])
  const [driveTypeFilter, setDriveTypeFilter] = useState<DriveTypeCombination[] | null>(null)
  const { data: requirementSubintervals } = useQuery({
    ...getRequirementSubintervalsOptions({
      path: {
        billing_period_id: billingPeriodId ?? 0,
        starting_from: viewStart,
        ending_at: viewEnd,
      },
    }),
    enabled: tabs.includes('requirements') && billingPeriodId !== null,
  })
  const extraChips = useMemo(() => {
    if (activeMode !== 'requirements' || !requirementSubintervals) {
      return []
    }
    const requirements = Object.values(requirementSubintervals.requirements)
    return buildRequirementIntervalChips({
      requirementSubintervals: requirementSubintervals.requirement_subintervals,
      windowStart: viewStart,
      windowEnd: viewEnd,
      overlappingBillingPeriods: overlapping,
      presets,
      requirements,
      selectedCombinations: driveTypeFilter ?? uniqueDriveTypeCombinations(requirements),
    }).map(chip => ({
      id: chip.id,
      interval: chip.interval,
      label: t('autoSchedule.requirementWindow', { range: chip.rangeLabel, count: chip.count }),
    }))
  }, [activeMode, requirementSubintervals, viewStart, viewEnd, overlapping, presets, driveTypeFilter, t])

  return (
    <Modal
      opened
      fullScreen
      zIndex={autoScheduleModalZIndex}
      onClose={requestClose}
      padding="lg"
      title={
        <Group gap="md" align="baseline" pr="md">
          <Title order={2} ff="monospace" size="h3">
            {vehicle?.id.toUpperCase() ?? vehicleId.toUpperCase()}
          </Title>
          <Text size="lg" c="dimmed">
            {headerDate}
          </Text>
        </Group>
      }
      styles={{
        inner: { padding: 0 },
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
        },
        header: {
          flexShrink: 0,
          padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
          borderBottom: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {tabs.length === 0 || !activeMode ? (
        <Stack gap="lg" style={{ flex: 1, minHeight: 0 }}>
          <Text c="dimmed">
            {t(isAutoScheduleAvailable(flags) ? 'autoSchedule.noBillingPeriod' : 'autoSchedule.noModesEnabled')}
          </Text>
          <Group justify="flex-end" pt="md">
            <Button variant="default" onClick={requestClose}>
              {t('cancel', { ns: 'common' })}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="lg" style={{ flex: 1, minHeight: 0 }}>
          {activeMode !== 'copy' && (
            <IntervalPicker
              windowStart={viewStart}
              windowEnd={viewEnd}
              interval={interval}
              onChange={setInterval}
              extraChips={extraChips}
            />
          )}
          <AutoScheduleTabList tabs={tabs} value={activeMode} onChange={changeMode} />
          <Box style={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative' }}>
            <Suspense fallback={<RouteLoading title={t('loading', { ns: 'common' })} />}>
              {tabs.includes('requirements') && billingPeriodId !== null ? (
                <Box h="100%" display={activeMode === 'requirements' ? 'block' : 'none'}>
                  <RequirementsMode
                    billingPeriodId={billingPeriodId}
                    vehicleId={vehicleId}
                    viewStart={viewStart}
                    viewEnd={viewEnd}
                    interval={interval}
                    onDirtyChange={dirty =>
                      setDirtyByTab(current =>
                        current.requirements === dirty ? current : { ...current, requirements: dirty }
                      )
                    }
                    onDriveTypeFilterChange={setDriveTypeFilter}
                    onFinished={onClose}
                    mapActive={activeMode === 'requirements'}
                  />
                </Box>
              ) : null}
              {tabs.includes('districts') ? (
                <Box h="100%" display={activeMode === 'districts' ? 'block' : 'none'}>
                  <DistrictsMode
                    vehicleId={vehicleId}
                    billingPeriodId={billingPeriodId ?? overlapping[0]?.id ?? null}
                    viewStart={viewStart}
                    viewEnd={viewEnd}
                    interval={interval}
                    onDirtyChange={dirty =>
                      setDirtyByTab(current =>
                        current.districts === dirty ? current : { ...current, districts: dirty }
                      )
                    }
                    onFinished={onClose}
                    mapActive={activeMode === 'districts'}
                  />
                </Box>
              ) : null}
              {tabs.includes('copy') ? (
                <Box h="100%" display={activeMode === 'copy' ? 'block' : 'none'}>
                  <CopyMode
                    vehicleId={vehicleId}
                    targetWindowStart={viewStart}
                    onDirtyChange={dirty =>
                      setDirtyByTab(current => (current.copy === dirty ? current : { ...current, copy: dirty }))
                    }
                    onFinished={onClose}
                  />
                </Box>
              ) : null}
            </Suspense>
          </Box>
          <Group
            justify="flex-end"
            pt="md"
            style={{
              flexShrink: 0,
              borderTop: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
            }}
          >
            <Button variant="default" onClick={requestClose}>
              {t('cancel', { ns: 'common' })}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}
